import axios from 'axios'
import type { TEventName } from 'types/tracker'
import UAParser from 'ua-parser-js'
import { v4 as uuidv4 } from 'uuid'

import type { TUserDataStore } from 'stores/userdata'
import { USER_DATA_LOCAL_STORAGE_NAME } from 'stores/userdata'

import { IS_TRACKER_TOGGLED_ON } from 'configs/toggle'

const basePath = process.env.NEXT_PUBLIC_TRACKER_URL

const saveEventTriggerAndTriggerIdToLocalStorage = (
  eventName: TEventName,
  id: string
) => {
  const trackerStore = window.localStorage.getItem('cur-fe-tracker')
  const { state: prevState = {}, version = 1 } = trackerStore
    ? JSON.parse(trackerStore)
    : {}

  window.localStorage.setItem(
    'cur-fe-tracker',
    JSON.stringify({
      state: {
        ...prevState,
        eventTrigger: eventName,
        eventTriggerId: id,
      },
      version: version || 1,
    })
  )
}

const getEventTriggerAndTriggerIdFromLocalStorage = () => {
  const trackerStore = window.localStorage.getItem('cur-fe-tracker')
  const { state = {} } = trackerStore ? JSON.parse(trackerStore) : {}
  const { eventTrigger, eventTriggerId } = state || {}
  return {
    ...(eventTrigger ? { event_trigger: eventTrigger } : {}),
    ...(eventTriggerId ? { event_trigger_id: eventTriggerId } : {}),
  }
}

const getUserDataFromLocalStorage = (): { state: TUserDataStore } => {
  const userDataLocalStorage = window?.localStorage?.getItem?.(
    USER_DATA_LOCAL_STORAGE_NAME
  )

  const userData: { state: TUserDataStore } = userDataLocalStorage
    ? JSON.parse(userDataLocalStorage)
    : { state: {} as TUserDataStore }

  return userData
}

export const getDefaultTrackerData = (isPlatformVerEnabled: boolean) => {
  const userData = getUserDataFromLocalStorage()
  const { device, os } = UAParser(window.navigator.userAgent)
  // eslint-disable-next-line unused-imports/no-unused-vars
  const { ip, connectionInfo } = userData?.state

  const deviceModel = (() => {
    if (device.vendor) {
      if (os.name === 'iOS') {
        return `iPhone`
      }
      return `${os?.name === 'android' ? 'Android' : os?.name} ${device.vendor}`
    }
    return `${os.name === 'Mac OS' ? 'MacOS' : os.name} ${
      os.name === 'Mac OS' ? 'Macbook' : 'unknown'
    }`
  })()

  return {
    platform: window.AndroidHandler ? 'APP' : 'WEB',
    ...(isPlatformVerEnabled ? { platform_version: os?.version } : {}),
    device_model: deviceModel,
    internet_connection:
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window?.navigator as any)?.connection?.effectiveType || 'unknown',
    // internet_provider: connectionInfo?.isp || '',
    referrer_url: document.referrer,
    timestamp: new Date().toISOString(),
    ip,
    // city: connectionInfo?.city || '',
    // regency: connectionInfo?.regionName || '',
  }
}

export const getTrackerUserData = () => {
  const userData = getUserDataFromLocalStorage()
  const { user } = userData?.state
  return {
    teacher_email: user?.email,
    province: user?.province,
  }
}

export type TTrackParams = {
  eventName: TEventName
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params?: Record<string, any>
  useDefaultData?: boolean
  useUserData?: boolean
  useTriggerData?: boolean
  updateTriggerData?: boolean
  isPlatformVerEnabled?: boolean
}

export const track = async ({
  eventName,
  params,
  useDefaultData = true,
  useUserData = true,
  useTriggerData = true,
  updateTriggerData = true,
  isPlatformVerEnabled = false,
}: TTrackParams) => {
  if (IS_TRACKER_TOGGLED_ON) {
    const eventId = uuidv4()
    const trackData = {
      id: eventId,
      ...(useDefaultData ? getDefaultTrackerData(isPlatformVerEnabled) : {}),
      ...(useUserData ? getTrackerUserData() : {}),
      ...(useTriggerData ? getEventTriggerAndTriggerIdFromLocalStorage() : {}),
      ...params,
    }
    const payload = {
      event_name: eventName,
      data: JSON.stringify(trackData),
    }
    if (process.env.NODE_ENV !== 'development') {
      const basicAuth =
        process.env.NEXT_PUBLIC_TRACKER_BASIC_AUTH ||
        btoa(
          unescape(
            encodeURIComponent(
              `${process.env.NEXT_PUBLIC_TRACKER_USERNAME}:${process.env.NEXT_PUBLIC_TRACKER_PASSWORD}`
            )
          )
        )
      try {
        await axios.post(basePath, payload, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Basic ${basicAuth}`,
          },
        })
      } catch (e) {
        console.error('error', e)
      }
    } else {
      // eslint-disable-next-line no-console
      console.log('track', payload)
    }
    if (updateTriggerData && eventName) {
      saveEventTriggerAndTriggerIdToLocalStorage(eventName, eventId)
    }
  }
}
